import React from 'react';
import { FaStar, FaShoppingCart, FaHeart, FaTag } from 'react-icons/fa';

const ProductListRenderer = ({ data, onAddToCart }) => {
  const { products, context } = data;

  const renderStars = (rating) => {
    if (!rating) return null;
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar 
            key={star} 
            className={`text-xs ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const getAvailabilityBadge = (availability) => {
    const styles = {
      'in_stock': 'bg-green-100 text-green-800 border-green-200',
      'out_of_stock': 'bg-red-100 text-red-800 border-red-200',
      'limited': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };

    const labels = {
      'in_stock': 'In Stock',
      'out_of_stock': 'Out of Stock',
      'limited': 'Limited Stock'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[availability] || styles.in_stock}`}>
        {labels[availability] || availability}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <FaTag className="text-blue-600" />
          <h3 className="font-semibold text-gray-900">Product Recommendations</h3>
        </div>
        {context && (
          <p className="text-sm text-gray-600 mt-1">{context}</p>
        )}
      </div>

      {/* Products grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {products.map((product) => (
            <div key={product.productId} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              {/* Product image */}
              <div className="relative">
                {product.image ? (
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <FaTag className="text-gray-400 text-4xl" />
                  </div>
                )}
                
                {/* Discount badge */}
                {product.originalPrice && product.originalPrice > product.price && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                    -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                  </div>
                )}

                {/* Wishlist button */}
                <button className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow">
                  <FaHeart className="text-gray-400 hover:text-red-500 text-sm" />
                </button>
              </div>

              {/* Product info */}
              <div className="p-4">
                <div className="mb-2">
                  {product.brand && (
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                      {product.brand}
                    </p>
                  )}
                  <h4 className="font-medium text-gray-900 line-clamp-2">{product.name}</h4>
                  {product.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                  )}
                </div>

                {/* Rating */}
                {product.rating && (
                  <div className="flex items-center space-x-2 mb-2">
                    {renderStars(product.rating)}
                    {product.reviewCount && (
                      <span className="text-xs text-gray-500">({product.reviewCount})</span>
                    )}
                  </div>
                )}

                {/* Price */}
                <div className="flex items-center space-x-2 mb-3">
                  <span className="font-semibold text-gray-900">
                    {product.currency} ${product.price ? product.price.toFixed(2) : '0.00'}
                  </span>
                  {product.originalPrice && product.price && product.originalPrice > product.price && (
                    <span className="text-sm text-gray-500 line-through">
                      ${product.originalPrice ? product.originalPrice.toFixed(2) : '0.00'}
                    </span>
                  )}
                </div>

                {/* Availability and actions */}
                <div className="flex items-center justify-between">
                  {getAvailabilityBadge(product.availability)}
                  <button 
                    onClick={() => onAddToCart && onAddToCart(product.productId)}
                    disabled={product.availability === 'out_of_stock'}
                    className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                  >
                    <FaShoppingCart className="text-xs" />
                    <span>Add to Cart</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductListRenderer;
